/**
 * Class with controls of objects
 */
import * as THREE from "three";
import Experience from "../../../Experience.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";

export default class InteractionManager {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.renderer = this.experience.renderer;
    this.camera = this.experience.camera;
    this.sizes = this.experience.sizes;
    this.resources = this.experience.resources;
    this.time = this.experience.time;
    this.debug = this.experience.debug;
    this.objectManager = this.experience.objectManager;
    this.room = this.experience.room;
    this.measures = this.experience.measures;
    this.heightWalls = this.room.heightWalls;
    this.menuManager = this.experience.menuManager;
    this.constraints = this.menuManager.constraints;

    this.selectedObject = null;
    this.pointerDownTime = 0;

    this.setTransformControl();

    this.raycaster = this.experience.raycaster;

    // Debug
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder("object");
    }

    this.pointer = new THREE.Vector2(Infinity, Infinity);
    this.boxHelper = null;
  }

  setTransformControl() {
    this.transforms = new TransformControls(
      this.camera.instance,
      this.renderer.instance.domElement
    );
    this.transforms.setMode("translate");

    this.transforms.addEventListener("dragging-changed", (event) => {
      if (!this.camera.is2DView) {
        this.camera.controls.enabled = !event.value;
      }
    });
    this.transforms.addEventListener("mouseDown", () => {
      this.camera.controls.enabled = false;
    });
    this.transforms.addEventListener("mouseUp", () => {
      this.camera.controls.enabled = true;
    });

    this.transforms.addEventListener("change", () => {
      if (this.selectedObject !== undefined && this.selectedObject !== null) {
        this.updateObjectPosition(
          this.selectedObject,
          this.selectedObject.userData.collision,
          this.selectedObject.userData.snapping
        );

        // Set light only on one side (north)
        if (this.selectedObject.name == "windowGroup") {
          if (this.selectedObject.rotation.y === -Math.PI / 2) {
            this.selectedObject.children[1].intensity = 1;
          } else {
            this.selectedObject.children[1].intensity = 0;
          }
        }
        this.measures.updateDistances(this.selectedObject);
      }
    });

    this.transforms.showY = false;

    this.transforms.traverse((child) => {
      if (child.material && child.material.color) {
        if (child.name === "X") {
          child.material.color.set(0x000000);
          child.material.opacity = 0.8;
        } else if (child.name === "Y") {
          child.material.color.set(0x000000);
          child.material.opacity = 0.8;
        } else if (child.name === "Z") {
          child.material.color.set(0x000000);
          child.material.opacity = 0.8;
        }
        child.material.transparent = true;
      }
    });

    this.scene.add(this.transforms);

    // Set size transform control
    this.updateTransformControlSize();
  }

  /**
   * Snap object on the wall and collison between objects
   */

  updateObjectPosition(modelClick, collision = true, snapping = true) {
    const snapDistance = 0.2;
    const initialY = modelClick.position.y;
    const wallsGroup = this.scene.getObjectByName("planeGroupWall").children;
    
    let hasSnappedToWall = false; // Pour éviter que la collision annule le snap

    // ÉTAPE 1 : Snapping avec les murs
    if (snapping && modelClick.name !== "showerWall") {
        const objectBox = new THREE.Box3().setFromObject(modelClick);

        wallsGroup.forEach((wall) => {
            const wallBox = new THREE.Box3().setFromObject(wall);

            if (objectBox.intersectsBox(wallBox)) {
                const clampedPosition = modelClick.position.clone();
                clampedPosition.x = THREE.MathUtils.clamp(
                    modelClick.position.x,
                    wallBox.min.x,
                    wallBox.max.x
                );
                clampedPosition.z = THREE.MathUtils.clamp(
                    modelClick.position.z,
                    wallBox.min.z,
                    wallBox.max.z
                );
                clampedPosition.y = initialY;

                const distance = modelClick.position.distanceTo(clampedPosition);

                if (distance < snapDistance) {
                    modelClick.position.copy(clampedPosition);
                    const wallNormal = new THREE.Vector3();
                    wall.getWorldDirection(wallNormal);
                    modelClick.lookAt(modelClick.position.clone().add(wallNormal));
                    hasSnappedToWall = true;
                }
            }
        });
    }

    // ÉTAPE 2 : Collisions entre objets (PAS affecté par hasSnappedToWall)
    if (collision) {
        const objectsToCheck = this.objectManager.getObjectsCollision();

        objectsToCheck.forEach((otherObject) => {
            if (otherObject === modelClick) return;

            if (
                (otherObject.name == "floor" ||
                    (otherObject.name == "showerPanel" && modelClick.name !== "showerFaucet") ||
                    otherObject.name == "partition") &&
                !collision
            ) {
                otherObject.updateMatrixWorld(true);
                modelClick.updateMatrixWorld(true);

                const otherBox = new THREE.Box3().setFromObject(otherObject);
                const objectBox = new THREE.Box3().setFromObject(modelClick);

                if (objectBox.intersectsBox(otherBox) && !hasSnappedToWall) {
                    if (this.transforms.object !== undefined) {
                        modelClick.position.copy(this.transforms.object.oldPosition);
                        modelClick.position.y = initialY;
                    }
                }
            } else if (collision && otherObject.name !== "wall") {
                otherObject.updateMatrixWorld(true);
                modelClick.updateMatrixWorld(true);

                const otherBox = new THREE.Box3().setFromObject(otherObject);
                const objectBox = new THREE.Box3().setFromObject(modelClick);

                if (objectBox.intersectsBox(otherBox) && !hasSnappedToWall) {
                    if (this.transforms.object !== undefined) {
                        modelClick.position.copy(this.transforms.object.oldPosition);
                        modelClick.position.y = initialY;
                    }
                }
            }
        });
    }

    // ÉTAPE 3 : Snapping entre objets
    if (snapping && collision && !hasSnappedToWall) {
        const objectsToCheck = this.objectManager
            .getObjects()
            .filter((obj) => obj !== modelClick);

        objectsToCheck.forEach((otherObject) => {
            if (
                modelClick.name === "showerWall" &&
                otherObject.name === "showerTray"
            ) {
                return;
            }
            const otherBox = new THREE.Box3().setFromObject(otherObject);

            const clampedPosition = modelClick.position.clone();
            clampedPosition.x = THREE.MathUtils.clamp(
                modelClick.position.x,
                otherBox.min.x,
                otherBox.max.x
            );
            clampedPosition.z = THREE.MathUtils.clamp(
                modelClick.position.z,
                otherBox.min.z,
                otherBox.max.z
            );

            clampedPosition.y = initialY;

            const distance = modelClick.position.distanceTo(clampedPosition);

            if (distance < snapDistance) {
                modelClick.position.copy(clampedPosition);
            }
        });
    }

    // ÉTAPE 4 : Vérification des limites de la pièce (TOUJOURS ACTIVE)
    if (!this.camera.is2DView && this.transforms.object !== undefined) {
        const objectBox = new THREE.Box3().setFromObject(modelClick);
        const wallsGroupBox = new THREE.Box3().setFromObject(
            this.scene.getObjectByName("planeGroupWall")
        );
        
        if (!modelClick.userData.collisionTimer) {
            modelClick.userData.collisionTimer = {
                startTime: null,
                allowExit: false,
            };
        }

        // Vérifier si l'objet sort de la pièce
        if (!wallsGroupBox.containsBox(objectBox)) {
            // Première fois qu'on détecte la sortie
            if (modelClick.userData.collisionTimer.startTime === null) {
                modelClick.userData.collisionTimer.startTime = performance.now();
            }

            // Calculer le temps écoulé
            const elapsed = performance.now() - modelClick.userData.collisionTimer.startTime;
            
            if (elapsed > 800) {
                modelClick.userData.collisionTimer.allowExit = true;
            }

            // Si on n'a pas attendu 800ms, bloquer la sortie
            if (!modelClick.userData.collisionTimer.allowExit) {
                modelClick.position.copy(this.transforms.object.oldPosition);
                modelClick.position.y = initialY;
            }
        } else {
            // L'objet est à l'intérieur, réinitialiser le timer
            modelClick.userData.collisionTimer.startTime = null;
            modelClick.userData.collisionTimer.allowExit = false;
            this.transforms.object.oldPosition.copy(modelClick.position);
        }
    }

    // ÉTAPE 5 : Sauvegarde finale de la position
    if (!this.camera.is2DView && this.transforms.object !== undefined) {
        modelClick.position.y = initialY;
        this.transforms.object.oldPosition.copy(modelClick.position);
    }

    // ÉTAPE 6 : Limites sur l'axe Y
    let sizeObject = this.getSizeOfObject(modelClick);
    
    if (modelClick.userData.axesMove) {
        if (
            modelClick.userData.axesMove[1] &&
            modelClick.position.y > this.heightWalls - sizeObject.y
        ) {
            modelClick.position.y = this.heightWalls - sizeObject.y;
        } else if (modelClick.position.y < 0) {
            modelClick.position.y = 0;
        }
    } else if (modelClick.name === "windowGroup") {
        sizeObject = this.getSizeOfObject(modelClick);
        const adjustedY = modelClick.position.y + modelClick.children[0].position.y;

        if (adjustedY > this.heightWalls - sizeObject.y) {
            modelClick.position.y =
                this.heightWalls - sizeObject.y - modelClick.children[0].position.y;
        } else if (adjustedY < 0) {
            modelClick.position.y = -modelClick.children[0].position.y;
        }
    }
}

  /**
   * set size of transform control
   */
  updateTransformControlSize() {
    const sizeFactor = 0.8; // size transform control
    this.transforms.setSize(sizeFactor);
  }

  /**
   * Get size of an object
   */
  getSizeOfObject(modelClick) {
    const boundingBox = new THREE.Box3().setFromObject(modelClick);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    return size;
  }

  /**
   * Click on object
   */
  onPointerDown(event) {
    if (event.target.closest(".protected") || !this.experience.active) {
      return; // Ignorer les clics sur les boutons dans la scène
    }

    document.getElementById("turnObject").classList.add("d-none");

    // Sauvegarde l'heure du clic
    this.pointerDownTime = Date.now();

    const normalizedPointer = this.experience.normalizePointer(event);
    this.pointer.x = normalizedPointer.x;
    this.pointer.y = normalizedPointer.y;

    if (this.camera.is2DView) {
      /* this.pointer.x = (event.clientX / this.sizes.width) * 2 - 1;
      this.pointer.y = -(event.clientY / this.sizes.height) * 2 + 1; */
      this.raycaster.setFromCamera(this.pointer, this.camera.orthographic);
    } else {
      this.raycaster.setFromCamera(this.pointer, this.camera.instance);
    }

    const intersects = this.raycaster.intersectObjects(
      this.objectManager.getObjects(),
      true
    );

    if (intersects.length > 0 && !this.camera.is2DView) {
      // if object intersect and view 3d
      //this.objectIsIntersectOn3dPlan(intersects[0].object);
    } else if (intersects.length > 0 && this.camera.is2DView) {
      this.selectedObject = intersects[0].object;
      this.selectedObject = this.selectedObject;

      this.measures.updateDistances(this.selectedObject);
    }
  }

  /**
   * On pointer up (objet)
   */
  onPointerUp(event) {
    if (event.target.closest(".protected")) {
      return; // Ignorer les clics sur les boutons dans la scène
    }
    if (!this.camera.is2DView) {
      const normalizedPointer = this.experience.normalizePointer(event);

      this.pointer.x = normalizedPointer.x;
      this.pointer.y = normalizedPointer.y;
      this.raycaster.setFromCamera(this.pointer, this.camera.instance);

      const clickDuration = Date.now() - this.pointerDownTime;

      // Si clic court (< 200ms), désélectionner si aucun objet n'est sous la souris
      if (clickDuration < 200) {
        const intersects = this.raycaster.intersectObjects(
          this.objectManager.getObjects(),
          true
        );
        if (intersects.length > 0 && !this.camera.is2DView) {
          if (this.room.getIsSelectedWall()) {
            this.room.detachSelectedWall();
            this.menuManager.closeContainerAboveMenu();
          }
          // if object intersect and view 3d
          if (intersects[0].object.userData.isBoxGeometry) {
            this.menuManager.openContainerChangeTexture(
              intersects[0].object.userData.type,
              intersects[0].object.userData.subType
            );
          }

          this.objectIsIntersectOn3dPlan(intersects[0].object);

          return;
        }
        if (intersects.length === 0) {
          this.detachSelectedObject();
          this.menuManager.closeContainerAboveMenu();
        }
      } else {
        // Clic long, ne rien faire si objet sélectionné
        this.moveButtonRotateObject();
      }

      // Réinitialisez `pointerDownTime` pour éviter les erreurs
      this.pointerDownTime = 0;
    }
  }

  /**
   * if object is intersect and user in on 3d plan
   */
  objectIsIntersectOn3dPlan(object) {
    // Si on clique sur une flèche de transformation
    if (this.transforms.dragging) {
      return; // On ne fait rien pour éviter de changer la sélection
    }
    this.scene.remove(this.boxHelper);

    this.selectedObject = object;

    /* this.menuManager.openContainerChangeTexture(
      this.selectedObject.userData.type
    ); */

    // Récupérer le parent principal
    while (
      this.selectedObject.parent &&
      this.selectedObject.parent !== this.scene
    ) {
      this.selectedObject = this.selectedObject.parent;
    }

    if (this.selectedObject && this.selectedObject.parent) {
      if (!this.selectedObject.oldPosition) {
        this.selectedObject.oldPosition = this.selectedObject.position.clone();
      }
      this.setAxiesTransformControl();
      this.transforms.attach(this.selectedObject);
      this.moveButtonRotateObject();
    }

    this.boxHelper = new THREE.BoxHelper(this.selectedObject, 0xffff00);
    this.scene.add(this.boxHelper);

    this.transforms.addEventListener("change", () => {
      this.boxHelper.update();
    });

    // Get name to show when object is selected (expetion for window because it's a group)
    if (this.selectedObject.name === "windowGroup") {
      document.getElementById("nameObjectSelected").innerHTML =
        this.constraints["window"].titleSelectedObject;
    } else {
      document.getElementById("nameObjectSelected").innerHTML =
        this.constraints[this.selectedObject.userData.type].titleSelectedObject;
    }

    document
      .getElementById("buttonsActionConfigurator")
      .classList.remove("d-none");

    // Button duplicate for partition and showerPanel
    if (
      this.selectedObject.userData.type !== "partition" &&
      this.selectedObject.userData.type !== "showerPanel"
    ) {
      document.getElementById("duplicateObjectSelect").classList.add("d-none");
    } else {
      document
        .getElementById("duplicateObjectSelect")
        .classList.remove("d-none");
    }
  }

  /**
   * Move button rotate object
   */
  moveButtonRotateObject() {
    if (this.selectedObject !== null) {
      const button = document.getElementById("turnObject");

      // Exception for showerGrabBar
      if (this.selectedObject.name === "showerGrabBar") {
        button.getElementsByTagName("img")[0].src =
          "./images/icons/btn_rotate2.png";
      } else {
        button.getElementsByTagName("img")[0].src =
          "./images/icons/btn_rotate.png";
      }
      const container = document.querySelector("canvas");
      const rect = container.getBoundingClientRect();
      const vector = new THREE.Vector3();
      vector.copy(this.selectedObject.position);
      vector.project(this.camera.instance);

      const x = ((vector.x + 1) / 2) * rect.width + rect.left;
      const y = ((1 - vector.y) / 2) * rect.height + rect.top;

      const offsetY = 100;
      button.style.borderRadius = "50px";
      button.style.padding = "0px";
      button.style.left = `${x}px`;
      button.style.top = `${y - offsetY}px`;
      button.classList.remove("d-none");
    }
  }

  /**
   * Remove selected object
   */
  removeObject() {
    if (this.selectedObject) {
      this.scene.remove(this.selectedObject);
      document.getElementById("turnObject").classList.add("d-none");

      // Delete object in tab
      this.objectManager.removeObject(this.selectedObject);

      this.selectedObject.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material.isMaterial) {
            child.material.dispose();
          } else {
            for (const material of child.material) {
              material.dispose();
            }
          }
        }
      });
      this.detachSelectedObject();

      this.measures.removeMeasuresOfObject();
    }
  }

  cleanObject(object) {
    if (object.userData.instance) {
      object.userData.instance = null;
    }
  }

  /**
   * Duplicate selected object
   */
  duplicateObject() {
    this.objectManager.cloneInstance(this.selectedObject);
  }

  update() {
    this.updateTransformControlSize();
  }

  detachSelectedObject() {
    if (this.selectedObject) {
      this.scene.remove(this.boxHelper);
      this.transforms.detach(this.selectedObject);
      this.selectedObject = null;
      this.measures.removeMeasuresOfObject();
      document
        .getElementById("buttonsActionConfigurator")
        .classList.add("d-none"); // hide button delete object
    }
  }

  getIsSelectedObject() {
    return this.selectedObject ? true : false;
  }

  getSelectedObject() {
    return this.selectedObject;
  }

  setSelectedObject(newSelectedObject) {
    this.selectedObject = newSelectedObject;
  }

  /**
   * Set axies control transform for selected object
   */
  setAxiesTransformControl() {
    if (
      (this.selectedObject !== undefined &&
        this.selectedObject.userData.axesMove != undefined) ||
      this.selectedObject.name === "windowGroup"
    ) {
      if (
        (this.selectedObject.userData.axesMove &&
          this.selectedObject.userData.axesMove[0]) ||
        this.selectedObject.name === "windowGroup"
      ) {
        this.transforms.showX = true;
      } else {
        this.transforms.showX = false;
      }
      if (
        (this.selectedObject.userData.axesMove &&
          this.selectedObject.userData.axesMove[1]) ||
        this.selectedObject.name === "windowGroup"
      ) {
        this.transforms.showY = true;
      } else {
        this.transforms.showY = false;
      }
      if (
        (this.selectedObject.userData.axesMove &&
          this.selectedObject.userData.axesMove[2]) ||
        this.selectedObject.name === "windowGroup"
      ) {
        this.transforms.showZ = true;
      } else {
        this.transforms.showZ = false;
      }
      // Default not Y axies
    } else {
      this.transforms.showX = true;
      this.transforms.showY = false;
      this.transforms.showZ = true;
    }
  }
}
